/**
 * Pure helpers for the PROFILE screen (web + mobile, design/exports/account/{web,mobile}-3-profile): the rank
 * card label and the stat tiles, both derived only from data the game actually has — RatingInfo
 * (OnlineClient.requestRating, mirrored into ui.net.rating) and the wallet's owned heads. Never invented numbers
 * (no touchdowns / best streak: not tracked). Tested in profile.test.ts.
 */
import { CHARACTER_IDS, type RatingInfo } from '@hf/shared';
import { rankTier, type RankTier } from './format';

/**
 * The rank card's state. RatingInfo absent (offline, or not fetched yet) is distinct from a fetched RatingInfo
 * with zero games (a real, unrated account): the UI shows "—" for the former and "UNRANKED" for the latter —
 * never a guessed tier or Elo.
 */
export type ProfileRank =
  | { readonly state: 'unknown' }
  | { readonly state: 'unranked'; readonly elo: number }
  | { readonly state: 'ranked'; readonly tier: RankTier; readonly elo: number };

export const profileRank = (rating: RatingInfo | undefined): ProfileRank => {
  if (!rating) return { state: 'unknown' };
  if (rating.games <= 0) return { state: 'unranked', elo: rating.rating };
  return { state: 'ranked', tier: rankTier(rating.rating), elo: rating.rating };
};

/**
 * Profile stat tiles: MATCHES / WINS / LOSSES / WIN RATE come from RatingInfo (null when it is absent); WIN RATE is
 * also null with zero games (nothing to divide). HEADS is owned-vs-total from the wallet, always known regardless
 * of connectivity.
 */
export interface ProfileStats {
  readonly matches: number | null;
  readonly wins: number | null;
  readonly losses: number | null;
  /** 0-100, rounded; null when there are no games to divide by (or RatingInfo is absent). */
  readonly winRatePct: number | null;
  readonly headsOwned: number;
  readonly headsTotal: number;
}

export const profileStats = (
  rating: RatingInfo | undefined,
  ownsHead: (id: string) => boolean,
): ProfileStats => ({
  matches: rating?.games ?? null,
  wins: rating?.wins ?? null,
  losses: rating?.losses ?? null,
  winRatePct: rating && rating.games > 0 ? Math.round((rating.wins / rating.games) * 100) : null,
  headsOwned: CHARACTER_IDS.filter(ownsHead).length,
  headsTotal: CHARACTER_IDS.length,
});
